import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import _ from "lodash";
import { useLazyQuery } from "@apollo/client";
import { RANGE_BATCH_DELAY_MS } from "../modules/ParticipantsConstants";

const INITIAL_BATCH_SIZE = 30;

const useVirtualizedBatchFetcher = ({
  ids,
  skip = false,
  batchQuery,
  mapBatchData,
  batchVariablesBuilder,
}) => {
  const [detailsMap, setDetailsMap] = useState({});
  const detailsMapRef = useRef({});

  const inFlightIds = useRef(new Set());
  const pendingIdsRef = useRef(new Set());
  const batchTimerRef = useRef(null);
  const generationRef = useRef(0);

  // ✅ FIX 3: prevent duplicate initial calls
  const hasFetchedInitialRef = useRef(false);

  // ✅ FIX 1: stable ids key (deep compare)
  const idsKey = useMemo(() => ids.join(","), [JSON.stringify(ids)]);

  const stableIds = useMemo(() => ids, [idsKey]); // eslint-disable-line

  const prevIdsKey = useRef(idsKey);

  useEffect(() => {
    if (prevIdsKey.current !== idsKey) {
      prevIdsKey.current = idsKey;

      generationRef.current += 1;
      detailsMapRef.current = {};
      setDetailsMap({});
      inFlightIds.current = new Set();
      pendingIdsRef.current = new Set();

      hasFetchedInitialRef.current = false;
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
        batchTimerRef.current = null;
      }
    }
  }, [idsKey]);

  useEffect(() => {
    return () => {
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
        batchTimerRef.current = null;
      }
    };
  }, []);

  const [fetchBatch] = useLazyQuery(batchQuery, {
    fetchPolicy: "cache-and-network",
  });

  // ✅ FIX 2: make stable
  const mergeBatchResults = useCallback(
    (data, requestedIds) => {
      const items = mapBatchData(data);

      setDetailsMap(prev => {
        const next = { ...prev };

        _.forEach(items, item => {
          if (_.get(item, "id")) next[item.id] = item;
        });

        detailsMapRef.current = next;
        return next;
      });

      _.forEach(requestedIds, id => inFlightIds.current.delete(id));
    },
    [mapBatchData]
  );

  const flushPendingBatch = useCallback(() => {
    const ids = Array.from(pendingIdsRef.current);
    pendingIdsRef.current = new Set();
    batchTimerRef.current = null;

    if (_.isEmpty(ids)) return;

    const dispatchGen = generationRef.current;
    const variables = batchVariablesBuilder(ids);

    fetchBatch({ variables })
      .then(({ data }) => {
        if (dispatchGen !== generationRef.current) return;
        mergeBatchResults(data, ids);
      })
      .catch(() => {
        if (dispatchGen !== generationRef.current) return;
        ids.forEach(id => inFlightIds.current.delete(id));
      });
  }, [fetchBatch, mergeBatchResults, batchVariablesBuilder]);

  const onRangeChanged = useCallback(
    ({ startIndex, endIndex }) => {
      if (skip) return;

      const visibleIds = stableIds.slice(startIndex, endIndex + 1);

      const toFetch = _.filter(
        visibleIds,
        id => !detailsMapRef.current[id] && !inFlightIds.current.has(id)
      );

      if (_.isEmpty(toFetch)) return;

      _.forEach(toFetch, id => {
        inFlightIds.current.add(id);
        pendingIdsRef.current.add(id);
      });

      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
      }

      batchTimerRef.current = setTimeout(
        flushPendingBatch,
        RANGE_BATCH_DELAY_MS
      );
    },
    [stableIds, flushPendingBatch, skip]
  );

  useEffect(() => {
    if (skip || _.isEmpty(stableIds) || hasFetchedInitialRef.current) return;

    hasFetchedInitialRef.current = true;

    const initialBatch = stableIds.slice(0, INITIAL_BATCH_SIZE);
    const dispatchGen = generationRef.current;

    _.forEach(initialBatch, id => inFlightIds.current.add(id));

    const variables = batchVariablesBuilder(initialBatch);

    fetchBatch({ variables })
      .then(({ data }) => {
        if (dispatchGen !== generationRef.current) return;
        mergeBatchResults(data, initialBatch);
      })
      .catch(() => {
        if (dispatchGen !== generationRef.current) return;
        initialBatch.forEach(id => inFlightIds.current.delete(id));
      });
  }, [stableIds, skip, fetchBatch, mergeBatchResults, batchVariablesBuilder]);

  return {
    onRangeChanged,
    detailsMap,
    setDetailsMap,
  };
};

export default useVirtualizedBatchFetcher;
